import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { formatISO } from 'date-fns/formatISO';

import { Flex } from '@reckon-web/flex';
import {
  FormValue,
  formStateToFormValue,
  getInitialState,
  isFormValueEqualToValue,
  resetForm,
  useForm,
  useSubmit,
} from '@reckon-web/forms';
import { Heading } from '@reckon-web/heading';
import { Button } from '@reckon-web/button';
import { Stack } from '@reckon-web/stack';
import { Strong } from '@reckon-web/strong';
import { Text } from '@reckon-web/text';
import { List } from '@reckon-web/list';
import { useToasts } from '@reckon-web/toast';
import { Box } from '@reckon-web/box';
import { LoadingDots } from '@reckon-web/loading';
import { ContentDialog } from '@reckon-web/modal-dialog';
import { makeId } from '@reckon-web/utils';

import { ISODate } from '../../common/date-picker-field';
import { TaskStatusEnum } from '../../work/types/task-status-enum';
import { Task, useQueryTasks } from '../../../hooks/use-query-tasks';
import {
  JobForm,
  JobFormExternalErrors,
  JobFormInitialValueInput,
  JobFormType,
  ValidatedJobFormValue,
  jobFormSchema,
} from '../form';
import { useCheckDupliateJobName } from '../../../hooks/use-check-duplicate-job-name';
import { Opts } from '../new-job-dialog';
import { Job, useQueryJob } from '../../../hooks/use-query-job';
import { incrementDate } from '../../../utils/date-utils';
import { CreateJobInput, useCreateJob } from '../../../data/useCreateJob';
import { JobRecurrenceResult } from '../../../data/useGetJob';
import {
  GetEntitiesByIdsItem,
  useGetEntitiesByIds,
} from '../../../data/useGetEntitiesByIds';
import { useTenantTimezoneConversion } from '../../../hooks/use-tenant-timezone';
import { ExportableJobDetails } from '../new-job';
import {
  GetJobStatusListResult,
  useGetJobStatusList,
} from '../../../data/useGetJobStatusList';
import type { CreateTaskInput } from '../../../data/useCreateTask';
import { JobStatusEnum } from '../types/job-status-enum';
import { RecurrenceFrequencyEnumValue } from '../types/recurrence-frequency-enum';

// This constant lets us answer "the error was in field X, where on the form is it?"
// Here is the good spot to do it, we are in control of making the mutation input
const createInputFieldsToFormFieldsMap: Record<
  keyof CreateJobInput,
  keyof FormValue<JobFormType> | undefined
> = {
  cloudId: undefined,
  correlationId: undefined,
  name: 'name',
  clientId: 'debtor',
  template: 'template',
  status: undefined,
  jobStatusId: undefined,
  entities: 'clients',
  staffId: 'staff',
  managerId: 'manager',
  partnerId: 'partner',
  assigneeId: 'assignee',
  budget: 'budget',
  dueDate: 'dueDate',
  startDate: 'startDate',
  recurrence: undefined, // my plan falls down about here - we have no one specific field for recurrence
  tasks: undefined, // and we don't even have tasks to display
};

type CloneErrorItem = {
  key: string;
  element: React.ReactNode;
};

function cloneJobForRecurrence(
  sourceJob: Job,
  sourceTasks: Task[],
  sourceEntities: GetEntitiesByIdsItem[],
  notStartedStatus: GetJobStatusListResult
): { job?: Job; tasks?: Task[]; errors: CloneErrorItem[] } {
  if (!sourceJob.recurrence?.enabled) {
    return {
      errors: [
        {
          key: `${sourceJob.cloudId}-recurrence-disabled`,
          element: 'Cannot recur a job that has no recurrence set',
        },
      ],
    };
  }
  if (
    !sourceJob.recurrence.recurrenceFrequency ||
    !sourceJob.recurrence.nextDateUtc
  ) {
    return {
      errors: [
        {
          key: `${sourceJob.cloudId}-invalid-recurrence-meta`,
          element: 'Cannot recur a job that has no recurrence properties set',
        },
      ],
    };
  }

  const jobId = uuidv4();
  const frequency = sourceJob.recurrence.recurrenceFrequency;

  // clone the recurrence first. We need it
  const jobCloneRecurrence: JobRecurrenceResult = {
    ...sourceJob.recurrence,
    nextDateUtc: incrementDate(sourceJob.recurrence.nextDateUtc, frequency),
  };

  const errors: CloneErrorItem[] = [];
  let jobClone = {
    // deliberately not spreading here, we have to be explicit about what properties we grab
    cloudId: jobId,
    budget: sourceJob.budget,
    // if you're wondering why we're not checking for "is the client deleted/enddated"
    // and throwing an error like we do with entities
    // https://redgum.atlassian.net/wiki/spaces/RKN/pages/1003487617/What%2Bdoes%2Borganisation%2Bperson%2Bend%2Bdate%2Bimpact%2Bin%2Bworkflow
    // "Recuring Job, exclude from notification list of Jobs to be recurred. This will be changed post initial release"
    // basically, they're never in the "this job can be recurred" list to begin with
    client: sourceJob.client,
    clientId: sourceJob.clientId,
    closedDate: null,
    completedDate: null,
    dueDate: incrementDate(sourceJob.dueDate, frequency),
    dueDateRaw: undefined, //  TODO  I don't think we need this? It's only for inline editing
    // Entities will be rebuilt
    entities: [] as Job['entities'],
    name: sourceJob.name + ' copy',
    recurrence: jobCloneRecurrence,
    startDate: incrementDate(sourceJob.startDate, frequency),
    startDateRaw: undefined, // TODO I don't think we need this? It's only for inline editing
    status: notStartedStatus,
    WfDeletedOnUTC: undefined,
    WfCreatedOnUTC: undefined,
  } as Job;

  // new job must have valid entities
  const jobEntities = new Map<string, GetEntitiesByIdsItem>();
  for (const sourceEntity of sourceEntities) {
    if (sourceEntity.endDateUtc || sourceEntity.isDeleted) {
      errors.push({
        key: `${sourceEntity.cloudId}-invalid-or-deleted`,
        element: (
          <Fragment>
            <Strong>{sourceEntity.name}</Strong> has been archived and
            won&rsquo;t be able to be included in this instance
          </Fragment>
        ),
      });
    } else {
      jobClone.entities!.push(sourceEntity.cloudId);
      jobEntities.set(sourceEntity.cloudId, sourceEntity);
    }
  }

  // new job must have a valid partner
  if (sourceJob.partner) {
    if (
      // TODO in the old system, we had handy canPartner canManager etc for users
      // consider reintroducing some helper functions so this logic can be shared
      // it's pretty simple, but still, just in case it gets more compliated
      sourceJob.partner.partner &&
      sourceJob.partner.active &&
      sourceJob.partner.licensed
    ) {
      jobClone.partner = sourceJob.partner;
      jobClone.partnerUserId = sourceJob.partnerUserId;
    } else if (!sourceJob.partner.active) {
      errors.push({
        key: `${sourceJob.partner.cloudId}-inactive`,
        element: (
          <Fragment>
            Partner <Strong>{sourceJob.partner.fullName}</Strong> is inactive,
            please select a new partner.
          </Fragment>
        ),
      });
    } else if (!sourceJob.partner.licensed) {
      errors.push({
        key: `${sourceJob.partner.cloudId}-unlicensed`,
        element: (
          <Fragment>
            Partner <Strong>{sourceJob.partner.fullName}</Strong> is unlicensed,
            please select a new partner.
          </Fragment>
        ),
      });
    } else if (!sourceJob.partner.partner) {
      errors.push({
        key: `${sourceJob.partner.cloudId}-invalid-not-partner`,
        element: (
          <Fragment>
            Partner <Strong>{sourceJob.partner.fullName}</Strong> cannot be set
            as partner, please select a new partner.
          </Fragment>
        ),
      });
    }
  }
  // new job must have a valid manager
  if (sourceJob.manager) {
    if (
      sourceJob.manager.manager &&
      sourceJob.manager.active &&
      sourceJob.manager.licensed
    ) {
      jobClone.manager = sourceJob.manager;
      jobClone.managerUserId = sourceJob.manager.cloudId;
    } else if (!sourceJob.manager.active) {
      errors.push({
        key: `${sourceJob.manager.cloudId}-inactive`,
        element: (
          <Fragment>
            Manager <Strong>{sourceJob.manager.fullName}</Strong> is inactive,
            please select a new manager.
          </Fragment>
        ),
      });
    } else if (!sourceJob.manager.licensed) {
      errors.push({
        key: `${sourceJob.manager.cloudId}-unlicensed`,
        element: (
          <Fragment>
            Manager <Strong>{sourceJob.manager.fullName}</Strong> is unlicensed,
            please select a new manager.
          </Fragment>
        ),
      });
    } else if (!sourceJob.manager.manager) {
      errors.push({
        key: `${sourceJob.manager.cloudId}-invalid-not-manager`,
        element: (
          <Fragment>
            Manager <Strong>{sourceJob.manager.fullName}</Strong> cannot be set
            as manager, please select a new manager.
          </Fragment>
        ),
      });
    }
  }
  // new job must have a valid staff
  if (sourceJob.staff) {
    if (
      // sourceJob.staff.canStaffJob &&
      sourceJob.staff.active &&
      sourceJob.staff.licensed
    ) {
      jobClone.staff = sourceJob.staff;
      jobClone.staffUserId = sourceJob.staff.cloudId;
    } else if (!sourceJob.staff.active) {
      errors.push({
        key: `${sourceJob.staff.cloudId}-inactive`,
        element: (
          <Fragment>
            Staff <Strong>{sourceJob.staff.fullName}</Strong> is inactive,
            please select a new staff.
          </Fragment>
        ),
      });
    } else if (!sourceJob.staff.licensed) {
      errors.push({
        key: `${sourceJob.staff.cloudId}-unlicensed`,
        element: (
          <Fragment>
            Staff <Strong>{sourceJob.staff.fullName}</Strong> is unlicensed,
            please select a new staff.
          </Fragment>
        ),
      });
    }
  }
  // new job must have a valid assignee
  if (sourceJob.assignee) {
    if (
      // sourceJob.assignee.canAssigneeJob &&
      sourceJob.assignee.active &&
      sourceJob.assignee.licensed
    ) {
      jobClone.assignee = sourceJob.assignee;
      jobClone.assigneeUserId = sourceJob.assignee.cloudId;
    } else if (!sourceJob.assignee.active) {
      errors.push({
        key: `${sourceJob.assignee.cloudId}-inactive`,
        element: (
          <Fragment>
            Assignee <Strong>{sourceJob.assignee.fullName}</Strong> is inactive,
            please select a new assignee.
          </Fragment>
        ),
      });
    } else if (!sourceJob.assignee.licensed) {
      errors.push({
        key: `${sourceJob.assignee.cloudId}-unlicensed`,
        element: (
          <Fragment>
            Assignee <Strong>{sourceJob.assignee.fullName}</Strong> is
            unlicensed, please select a new assignee.
          </Fragment>
        ),
      });
    }
  }

  const tasksClone: Task[] = [];
  for (const sourceTask of sourceTasks) {
    if (sourceTask.wfDeletedOnUTC) {
      // deleted, nothing to do
    } else if (
      sourceTask.entity &&
      !jobEntities.has(sourceTask.entity.cloudId)
    ) {
      // entity isn't part of the job, can't include it
      errors.push({
        key: `${sourceTask.cloudId}-${sourceTask.entity.cloudId}-missing`,
        element: (
          <Fragment>
            Client for task <Strong>{sourceTask.name}</Strong> not in job, task
            will not be copied.
          </Fragment>
        ),
      });
    } else {
      // not deleted, entity included (or there is no entity)
      const newTask = {
        cloudId: uuidv4(),
        completedDate: null,
        description: sourceTask.description,
        dueDate: incrementDate(sourceTask.dueDate, frequency) || null,
        entity: sourceTask.entity,
        entityId: sourceTask.entityId,
        estimateMinutes: sourceTask.estimateMinutes,
        job: {
          cloudId: jobId,
          status: notStartedStatus,
        },
        jobId: jobId,
        name: sourceTask.name,
        status: TaskStatusEnum.valuesEnum.NotStarted,
        taskTypeId: sourceTask.taskTypeId,
        wfDeletedOnUTC: null,
      } as Task;

      if (sourceTask.assignee) {
        if (sourceTask.assignee.active && sourceTask.assignee.licensed) {
          newTask.assignee = sourceTask.assignee;
          newTask.assignedToUserId = sourceTask.assignedToUserId;
        } else if (!sourceTask.assignee.active) {
          errors.push({
            key: `${sourceTask.cloudId}-${sourceTask.assignee.cloudId}-inactive`,
            element: (
              <Fragment>
                Task <Strong>{sourceTask.name}</Strong> assignee{' '}
                <Strong>{sourceTask.assignee.fullName}</Strong> is inactive,
                please select a new assignee..
              </Fragment>
            ),
          });
        } else if (!sourceTask.assignee.licensed) {
          errors.push({
            key: `${sourceTask.cloudId}-${sourceTask.assignee.cloudId}-unlicensed`,
            element: (
              <Fragment>
                Task <Strong>{sourceTask.name}</Strong> assignee{' '}
                <Strong>{sourceTask.assignee.fullName}</Strong> is unlicensed,
                please select a new assignee..
              </Fragment>
            ),
          });
        }
      }
      tasksClone.push(newTask);
    }
  }

  return { job: jobClone, tasks: tasksClone, errors };
}

export type NewCloseArgs =
  | {
      didCancel: true;
    }
  | {
      didCancel: false;
      jobId: string;
    };

export type NewProps = {
  onClose: (args: NewCloseArgs) => void;
  opts?: Opts;
  setTasks: (tasks: Task[]) => void;
  tasks?: Task[];
  setShouldPromptUnsaved: (value: boolean) => void;
  setExportableJobDetails: (value?: ExportableJobDetails) => void;
  testId?: string;
};

// the values below are VERY specifically chosen to mimic what an empty form looks like. Can't just be all null or undefined :(
// surely there's a way of doing this - some kind of getInitialValue?
// Failing that, a useMemo with no deps to get the .value out of the form (would have to bypass the exhaustive deps checks)
// (optionally) a useEffect to reset this empty value back into the form. That'd be a bit silly, but might be needed
// then another useeffect
// I'm doing it this way for now, but next one to be near this code and Mitchell (or has some spare time)
// see if this potentially brittle hodgepodge of magic values can be cleaned up. It works. That's about the best that can be said for it
const newJobFormData: JobFormInitialValueInput = {
  assignee: undefined,
  budget: '',
  clients: [],
  debtor: null,
  dueDate: undefined,
  manager: undefined,
  name: '',
  partner: undefined,
  recurrenceEnabled: false,
  recurrenceFrequency: undefined,
  recurrenceNextInstance: undefined,
  staff: undefined,
  startDate: undefined,
  template: undefined,
} as JobFormInitialValueInput;

export const New: React.FC<NewProps> = ({
  onClose,
  opts,
  setTasks,
  tasks,
  setShouldPromptUnsaved,
  setExportableJobDetails,
  testId = `new-job__index`,
}) => {
  const [initialFormData, setInitialFormData] =
    useState<JobFormInitialValueInput>(() => newJobFormData);
  const jobId = opts?.mode === 'recur' ? opts.recurJobId : undefined;

  const { addToast } = useToasts();
  const { localDateToTenantToUtc } = useTenantTimezoneConversion();

  const { loading: loadingJobStatuses, data: statuses } = useGetJobStatusList();
  const notStartedStatus = useMemo(() => {
    return statuses?.find(
      (x) => x.status === JobStatusEnum.valuesEnum.NotStarted
    );
  }, [statuses]);

  const queryJobArgs: Parameters<typeof useQueryJob>[0] = useMemo(() => {
    return { variables: { cloudId: jobId || '' }, skip: !jobId };
  }, [jobId]);

  const {
    loading: loadingJob,
    // error: errorJob,
    data: job,
  } = useQueryJob(queryJobArgs);

  // queryJob returns basic task info. But we need FULL BERRIES task info
  const querytasksArgs: Parameters<typeof useQueryTasks>[0] = useMemo(() => {
    return jobId
      ? { variables: { myWork: false, jobs: [jobId] } }
      : { skip: true };
  }, [jobId]);
  const {
    loading: loadingTasks,
    // error: errorTasks,
    data: jobTasks,
  } = useQueryTasks(querytasksArgs);

  // ugh. We also need the entities, they're not loaded by anything
  const getEntitiesByIdsArgs: Parameters<typeof useGetEntitiesByIds>[0] =
    useMemo(() => {
      return { variables: { cloudIds: job?.entities || [] }, skip: !job };
    }, [job]);
  const {
    loading: loadingEntities,
    // error: errorEntities,
    data: jobEntities,
    // I'd like to use refetch here, but queryTasks and queryJob is not refetchable
    // so I'll go with the normal pattern
    // refetch: refetchEntities,
    // what I did learn though is refetch returns something with data/loading/networkstatus/error though
  } = useGetEntitiesByIds(getEntitiesByIdsArgs);

  // holders for displaying errors when cloning
  // Probably should put them in a content dialog
  const [cloneErrors, setCloneErrors] = useState<CloneErrorItem[]>();
  const [isCloneErrorsOpen, setIsCloneErrorsOpen] = useState<boolean>();
  const [hasCloned, setHasCloned] = useState<boolean>(false);

  useEffect(() => setHasCloned(false), [opts]);

  useEffect(() => {
    if (hasCloned) {
      return;
    }
    if (!job || !jobTasks || !jobEntities || !notStartedStatus) {
      return;
    }
    // we have a job. Goody. Clone it for recurrence, turn it into an input
    // and send the tasks out to the parent (it'll pass them straight back into us)
    setHasCloned(true);
    const {
      job: jobClone,
      tasks: tasksClone,
      errors,
    } = cloneJobForRecurrence(job, jobTasks, jobEntities, notStartedStatus);

    setCloneErrors(errors);
    setIsCloneErrorsOpen(errors.length > 0);

    if (!tasksClone || !jobClone) {
      // didn't actually get a clone back. Show toast, then close the whole dialog
      addToast({ tone: 'critical', title: 'Unable to recur job' });
      onClose({ didCancel: true });
      return;
    }

    // TODO this was lifted from the edit form. Really should be in some kind of shared function somewhere to turn a job into a form input
    const jobFormData: JobFormInitialValueInput = {
      debtor: {
        label: jobClone.client?.name || '',
        value: jobClone.clientId || '',
      },
      clients: jobClone.entities || undefined,
      budget: jobClone.budget,
      dueDate:
        jobClone.dueDate &&
        (formatISO(jobClone.dueDate, { representation: 'date' }) as ISODate),
      manager: jobClone.managerUserId,
      name: jobClone.name,
      partner: jobClone.partnerUserId,
      recurrenceEnabled: jobClone.recurrence?.enabled,
      recurrenceFrequency: jobClone.recurrence?.recurrenceFrequency,
      recurrenceNextInstance:
        jobClone.recurrence?.nextDateUtc &&
        (formatISO(jobClone.recurrence?.nextDateUtc, {
          representation: 'date',
        }) as ISODate),
      staff: jobClone.staffUserId,
      assignee: jobClone.assigneeUserId,
      startDate:
        jobClone.startDate &&
        (formatISO(jobClone.startDate, { representation: 'date' }) as ISODate),
      template: jobClone.templateId,
    };

    setInitialFormData(jobFormData);
    setTasks(tasksClone || []);
    // The only time we really want this to fire is when the job, tasks and entities are loaded
    // the rest are for exhaustive dep checking
    // onClose in particular is problematic, it changes when the dirty state changes
  }, [
    job,
    jobTasks,
    jobEntities,
    setTasks,
    addToast,
    onClose,
    hasCloned,
    notStartedStatus,
  ]);

  const form = useForm(jobFormSchema, initialFormData);
  useEffect(
    () => {
      resetForm(form, initialFormData);
    },
    // Mitchell says this is fine because the only thing resetForm uses is form.setState
    // which doesn't depend on anything other than the schema passed to useForm which won't change
    // ideally we could pass in form.setState(and then probably use form.setState directly) but
    // form.setState isn't technically stable(as in between renders, not stable as in API, it is a stable API)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialFormData]
  );
  const isFormEqual = isFormValueEqualToValue(
    form,
    formStateToFormValue(
      jobFormSchema,
      getInitialState(jobFormSchema, initialFormData)
    )
  );
  useEffect(
    () => setShouldPromptUnsaved(!isFormEqual),
    [setShouldPromptUnsaved, isFormEqual]
  );

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [createErrors, setCreateErrors] = useState<JobFormExternalErrors>();
  const [createJob] = useCreateJob();
  const [CheckDupliateNameDialog, checkDuplicateName] =
    useCheckDupliateJobName();

  const doSubmit = useCallback(
    async (value: ValidatedJobFormValue) => {
      setIsSaving(true);

      const canSaveBasedOnDupeNameCheck = await checkDuplicateName({
        jobId: undefined,
        jobName: value.name,
        debtorId: value.debtor.value,
        debtorName: value.debtor.label,
      });
      if (!canSaveBasedOnDupeNameCheck) {
        setIsSaving(false);
        return;
      }

      // I think what'll happen here is if it's empty, it'll be '' but if it is filled in and valid it'll be a number?
      const budget = typeof value.budget === 'number' ? value.budget : null;

      // by the time we get to here, validation has run and we KNOW that some of these values are filled in
      // so we can sling around ! and tell the compiler "I know better than you"
      const jobCloudId = uuidv4();
      const input = {
        cloudId: jobCloudId,
        name: value.name,
        clientId: value.debtor.value,
        template: value.template,
        jobStatusId:
          (opts?.mode === 'create' && opts?.statusId) ||
          notStartedStatus?.cloudId,
        entities: value.clients,
        staffId: value.staff,
        partnerId: value.partner,
        managerId: value.manager,
        assigneeId: value.assignee,
        budget,
        startDate: localDateToTenantToUtc(value.startDate) || null, // && parseISO(value.startDate),
        dueDate: localDateToTenantToUtc(value.dueDate) || null, // && parseISO(value.dueDate),
        recurrence: {
          cloudId: job?.recurrence?.cloudId || uuidv4(), //when recurring a job, need to retain the recurrence id
          enabled: value.recurrenceEnabled,
          frequency: value.recurrenceEnabled
            ? (value.recurrenceFrequency as RecurrenceFrequencyEnumValue)
            : null,
          startDate: value.recurrenceEnabled
            ? localDateToTenantToUtc(value.recurrenceNextInstance) || null // && parseISO(value.recurrenceNextInstance)
            : null,
        },
        tasks:
          tasks &&
          tasks.map(
            (t) =>
              ({
                cloudId: t.cloudId,
                jobId: jobCloudId,
                entityId: t.entity && t.entity.cloudId,
                name: t.name,
                assignedTo: t.assignedToUserId,
                status: t.status,
                taskTypeId: t.taskTypeId,
                // TODO need a consistent story for the dates here
                // they're not coming from a datepicker
                // they're coming from EITHER recurring a job, which will load the tasks then alter the due date
                // or from inflating a template, which (at the time of writing) does not have a due date
                // So.. depending on what we do for the conversion of LOADED values, these might not need conversion at all
                // If we convert the loaded to local time (my preference) then we need to convert here
                dueDate: t.dueDate,
                estimateMinutes: t.estimateMinutes,
              }) as CreateTaskInput
          ),
      };

      const gqlResult = await createJob({
        variables: { input },
      });

      if (
        gqlResult &&
        gqlResult.data &&
        gqlResult.data.createJob &&
        (!gqlResult.data.createJob.validationMessage ||
          gqlResult.data.createJob.validationMessage.length === 0)
      ) {
        // happy path
        // No toast, we transition to the view screen straight away

        setIsSaving(false);
        onClose({ didCancel: false, jobId: input.cloudId });
        return;
      }

      const validationErrors =
        gqlResult?.data?.createJob?.validationMessage || [];

      const alertMessages = [];
      let hasSpecificErrors = false;
      let hasNonspecificErrors = false;
      const externalErrors: JobFormExternalErrors = {};

      for (const message of validationErrors) {
        // look up where this particular input models property went in the field
        const formField =
          createInputFieldsToFormFieldsMap[
            message.field as keyof CreateJobInput
          ];
        if (formField) {
          // it went somewhere, yay. Tuck away the error message for later display
          externalErrors[formField] = externalErrors[formField] || [];
          externalErrors[formField]?.push(message.message || '');
          hasSpecificErrors = true;
        } else {
          // not for any specific field we know about. Might be a cross field message, might be no field at all
          // worst case someone has renamed a field in the graphql but forgot to update the validation messages
          if (message.field && message.message) {
            alertMessages.push(`${message.field}: ${message.message}`);
          } else if (message.message) {
            alertMessages.push(message.message);
          }
          hasNonspecificErrors = true;
        }
      }

      if (!hasSpecificErrors && !hasNonspecificErrors) {
        // got nothing? Probably means the array or one of the objects carrying it itself was null
        alertMessages.push(
          `Workflow failed to create the job. Try again or contact support.`
        );
      } else {
        // got a mix, or got only specific, or got only non specific
        // any of these cases, put the same message at the end of the array (which might be empty)
        alertMessages.push('Please fix the errors and try again');
      }
      setCreateErrors(externalErrors);

      addToast({
        title: `Job not created`,
        message: alertMessages.join(),
        tone: 'critical',
      });
      setIsSaving(false);
    },
    [
      addToast,
      createJob,
      onClose,
      opts,
      tasks,
      checkDuplicateName,
      job,
      localDateToTenantToUtc,
      notStartedStatus,
    ]
  );

  const submit = useSubmit(form, doSubmit);

  const doSave = () => {
    setCreateErrors(undefined);
    submit();
  };

  useEffect(
    () =>
      setExportableJobDetails({
        jobName: form.value.name,
        debtorName: form.value.debtor?.label,
      }),
    [form.value.debtor, form.value.name, setExportableJobDetails]
  );

  return (
    <Fragment>
      <Flex
        direction="column"
        width="100%"
        as="form"
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          doSave();
        }}
        data={{ testid: testId }}
      >
        <Box paddingX="xlarge" paddingY="large">
          <Heading level="3">Add new job</Heading>
        </Box>
        <Box flex="1 1 100%" overflowY="auto">
          {loadingJob ||
          loadingTasks ||
          loadingEntities ||
          loadingJobStatuses ? (
            <Box paddingX="xlarge" paddingY="large">
              <LoadingDots
                label="Loading job"
                data-testid={makeId(testId, 'loading-jobs')}
              />
            </Box>
          ) : (
            <JobForm
              form={form}
              externalErrors={createErrors}
              setTasks={setTasks}
              mode={opts?.mode || 'create'}
              formInitialValues={initialFormData}
            />
          )}
        </Box>
        <Stack flex="0 0 auto" paddingX="xlarge" paddingY="large" gap="small">
          <Button
            label="Create job"
            size="medium"
            loading={isSaving}
            // no onClick, look in the onSubmit of the form
            type="submit"
            data-testid={makeId(testId, 'create-job')}
          />
          <Button
            label="Cancel"
            size="medium"
            weight="none"
            tone="passive"
            onClick={() => onClose({ didCancel: true })}
            disabled={isSaving}
            data-testid={makeId(testId, 'cancel')}
          />
        </Stack>
      </Flex>
      <CheckDupliateNameDialog />
      <ContentDialog
        isOpen={isCloneErrorsOpen || false}
        onClose={() => setIsCloneErrorsOpen(false)}
        size="medium"
        header={
          <Box paddingX="xlarge" paddingY="large">
            <Heading level="2">Problem recurring job</Heading>
          </Box>
        }
        footer={
          <Flex justifyContent="flex-end" paddingX="xlarge" paddingY="large">
            <Button
              onClick={() => setIsCloneErrorsOpen(false)}
              label="Close"
              data-testid={makeId(testId, 'close')}
            />
          </Flex>
        }
      >
        <Stack paddingX="xlarge" paddingY="large" gap="small">
          <Text as="p" data={{ testid: makeId(testId, 'paragraph') }}>
            We found the following problems when recurring this job
          </Text>
          <List data={{ testid: makeId(testId, 'list') }}>
            {cloneErrors?.map((error) => (
              <Text key={error.key}>{error.element}</Text>
            ))}
          </List>
        </Stack>
      </ContentDialog>
    </Fragment>
  );
};
